import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box } from '@mui/system';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { MoneyOff, } from '@mui/icons-material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';


const CircularAnimatedChart = ({ onTotalAmountChange }) => {
  const [user, setUser] = useState({});
  const [token, setToken] = useState(() => localStorage.getItem('token') || null);
  const [fetchedData, setFetchedData] = useState([]);
  const [fetchedExpenses, setFetchedExpenses] = useState([]);
  const [fetchedDamageData, setFetchedDamageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dayByDayData, setDayByDayData] = useState([]);
  const [activeDay, setActiveDay] = useState(null);
  const isSmallScreen = useMediaQuery('(max-width: 600px)');
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', token);
    }
  }, [token]);

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        }
      }
    };

    fetchProfile();
  }, [navigate]);

  useEffect(() => {
    if (user.id) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-data');
          const nisalOrders = response.data.filter(
            (order) => order.usertoken.toLowerCase() === user.id.toString().toLowerCase()
          );
          setFetchedData(nisalOrders);
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          setLoading(false);
        }
      };

      const fetchExpenses = async () => {
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-expense-data');
          const nisalOrders = response.data.filter(
            (expense) => expense.usertoken.toLowerCase() === user.id.toString().toLowerCase()
          );
          setFetchedExpenses(nisalOrders);
        } catch (error) {
          console.error('Error fetching expenses data:', error);
        }
      };

      const fetchDamageData = async () => {
        try {
          const response = await axios.get('https://admin.bizstor.lk/public/api/get-damage-data');
          const nisalOrders = response.data.filter(
            (damage) => damage.usertoken.toLowerCase() === user.id.toString().toLowerCase() && damage.type === 'money back'
          );
          setFetchedDamageData(nisalOrders);
        } catch (error) {
          console.error('Error fetching damage data:', error);
        }
      };

      fetchData();
      fetchExpenses();
      fetchDamageData();
    }
  }, [user.id]);

  useEffect(() => {
    if (!fetchedData.length && !fetchedExpenses.length && !fetchedDamageData.length) return;

    const now = new Date();
    const currentDay = now.getDay();
    setActiveDay(currentDay === 0 ? 6 : currentDay - 1);
    
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const salesTotals = Array(7).fill(0);
    const expenseTotals = Array(7).fill(0);
    const damageTotals = Array(7).fill(0);

    const getDayIndex = (date) => {
      const dayOfWeek = date.getDay();
      return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    };

    fetchedData.forEach((order) => {
      const createdAt = new Date(order.invoicedate);
      if (createdAt >= startOfWeek && createdAt <= endOfWeek) {
        const dayIndex = getDayIndex(createdAt);
        let orderTotal = 0;

        order.items.forEach((item) => {
          const itemPrice = Number(item.price);
          const itemTax = item.tax || 0;
          const itemTotal = itemPrice + itemPrice * (itemTax / 100);
          orderTotal += itemTotal;
        });

        const discountPercentage = order.discount || 0;
        const discountAmount = (orderTotal * discountPercentage) / 100;
        orderTotal -= discountAmount;

        salesTotals[dayIndex] += orderTotal;
      }
    });

    fetchedExpenses.forEach((expense) => {
      const createdAt = new Date(expense.date);
      if (createdAt >= startOfWeek && createdAt <= endOfWeek) {
        const dayIndex = getDayIndex(createdAt);

        expense.items.forEach((item) => {
          expenseTotals[dayIndex] += Number(item.amount);
        });
      }
    });

    fetchedDamageData.forEach((damage) => {
      const createdAt = new Date(damage.invoicedate);
      if (createdAt >= startOfWeek && createdAt <= endOfWeek) {
        const dayIndex = getDayIndex(createdAt);

        damage.items.forEach((item) => {
          damageTotals[dayIndex] += Number(item.price);
        });
      }
    });

    const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const data = daysOfWeek.map((day, index) => ({
      name: day,
      sales: salesTotals[index],
      expenses: expenseTotals[index],
      return: damageTotals[index],
      total: (salesTotals[index] + expenseTotals[index] + damageTotals[index]) || 1
    }));

    setDayByDayData(data);

    if (onTotalAmountChange) {
      onTotalAmountChange(data);
    }
  }, [fetchedData, fetchedExpenses, fetchedDamageData, onTotalAmountChange]);

  // Format numbers with currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'Lkr',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        //boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        padding: '24px',
        borderRadius: '16px',
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      {loading ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'white',
            zIndex: 9999,
          }}
        >
          <l-trio size="50" speed="1.3" color="red"></l-trio>
        </Box>
      ) : (
        <Box sx={{ width: '100%' }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{
                color: '#2D3748',
                textAlign: 'center',
                fontWeight: 700,
                mb: 4,
                letterSpacing: '0.5px'
              }}
            >
              Weekly Financial Overview
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              gap: 2,
              mb: 4
            }}>
              {dayByDayData.map((day, index) => (
                <motion.div
                  key={day.name}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setActiveDay(index)}
                  style={{
                    cursor: 'pointer',
                    background: activeDay === index ? '#EBF8FF' : '#F7FAFC',
                    borderRadius: '12px',
                    padding: '8px 16px',
                    border: `2px solid ${activeDay === index ? '#63B3ED' : 'transparent'}`,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Typography sx={{ 
                    fontWeight: activeDay === index ? 700 : 500,
                    color: activeDay === index ? '#2B6CB0' : '#4A5568'
                  }}>
                    {day.name}
                  </Typography>
                </motion.div>
              ))}
            </Box>
          </motion.div>

          {activeDay !== null && dayByDayData[activeDay] && (
            <motion.div
              key={activeDay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isSmallScreen ? 'column' : 'row',
                gap: 4,
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                {/* Circular chart visualization */}
                <Box sx={{ 
                  position: 'relative',
                  width: 280,
                  height: 280,
                }}>
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ color: '#2D3748', fontWeight: 700 }}>
                      {dayByDayData[activeDay].name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#718096' }}>
                      Total Activity
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, color: '#4299E1' }}>
                      {(dayByDayData[activeDay].sales + dayByDayData[activeDay].expenses + dayByDayData[activeDay].return)}
                    </Typography>
                  </Box>
                  
                  {/* Sales arc */}
                  <SvgArc
                    percentage={dayByDayData[activeDay].sales / dayByDayData[activeDay].total * 100}
                    color="#9F7AEA"
                    trackColor="#EDF2F7"
                    size={280}
                    strokeWidth={20}
                    rotation={0}
                    delay={0.3}
                  />
                  
                  {/* Expenses arc */}
                  <SvgArc
                    percentage={dayByDayData[activeDay].expenses / dayByDayData[activeDay].total * 100}
                    color="#F56565"
                    trackColor="#EDF2F7"
                    size={220}
                    strokeWidth={20}
                    rotation={dayByDayData[activeDay].sales / dayByDayData[activeDay].total * 360}
                    delay={0.6}
                  />
                  
                  {/* Returns arc */}
                  <SvgArc
                    percentage={dayByDayData[activeDay].return / dayByDayData[activeDay].total * 100}
                    color="#FFB300"
                    trackColor="#EDF2F7"
                    size={160}
                    strokeWidth={20}
                    rotation={(dayByDayData[activeDay].sales + dayByDayData[activeDay].expenses) / dayByDayData[activeDay].total * 360}
                    delay={0.9}
                  />
                </Box>
                
                {/* Stats cards */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 3,
                  width: isSmallScreen ? '100%' : 'auto'
                }}>
                  <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <StatCard 
                      title="Sales"
                      value={dayByDayData[activeDay].sales}
                      color="#9F7AEA"
                      icon={<TrendingUpIcon/>}
                    />
                  </motion.div>
                  
                  <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <StatCard 
                      title="Expenses"
                      value={dayByDayData[activeDay].expenses}
                      color="#F56565"
                      icon={<MoneyOff/>}
                    />
                  </motion.div>
                  
                  <motion.div
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.5 }}
                  >
                    <StatCard 
                      title="Returns"
                      value={dayByDayData[activeDay].return}
                      color="#FFB300"
                      icon={<KeyboardReturnIcon/>}
                    />
                  </motion.div>
                </Box>
              </Box>
            </motion.div>
          )}
        </Box>
      )}
    </Box>
  );
};

// Component for the circular arcs
const SvgArc = ({ percentage, color, trackColor, size, strokeWidth, rotation = 0, delay = 0 }) => {
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const arc = circumference * (percentage / 100);
  
  return (
    <Box sx={{ 
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: size,
      height: size,
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference}
          strokeLinecap="round"
          transform={`rotate(${rotation}, ${center}, ${center})`}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: circumference - arc }}
          transition={{ 
            duration: 1.5, 
            delay: delay,
            ease: "easeInOut" 
          }}
        />
      </svg>
    </Box>
  );
};

// Component for stat cards
const StatCard = ({ title, value, color, icon }) => {
  return (
    <Box sx={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      //boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
      border: '1px solid #EDF2F7'
    }}>
      <Box sx={{
        width: 40,
        height: 40,
        borderRadius: '8px',
        backgroundColor: `${color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px'
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: '#718096' }}>
          {title}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D3748' }}>
          {new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'Lkr',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(value)}
        </Typography>
      </Box>
    </Box>
  );
};

export default CircularAnimatedChart;